import React from 'react';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { CaretRightIcon } from '@phosphor-icons/react';

export interface ContextNavigationNode {
  '@id'?: string;
  title: string;
  href?: string;
  type?: string;
  description?: string;
  is_current?: boolean;
  is_in_path?: boolean;
  review_state?: string;
  hidden?: boolean;
  items?: ContextNavigationNode[];
}

interface ContextNavigationLinkProps {
  props: ContextNavigationNode;
  submenu?: boolean;
  // Título de um item com filhos: a setinha fica no botão ao lado.
  folder?: boolean;
  // Item com filhos que leva ao submenu dele: ganha o "›".
  drill?: boolean;
  // Título já formatado (ex.: com o trecho do filtro em destaque).
  label?: React.ReactNode;
}

export const ContextNavigationLink: React.FC<ContextNavigationLinkProps> = ({
  props,
  submenu = false,
  folder = false,
  drill = false,
  label,
}) => {
  const isActive = !!props.is_current;

  // O estado ativo (página atual) é um modificador à parte do estado de
  // publicação, aplicado direto no link (e não no <li> pai) para que o fundo
  // navy cubra a linha inteira, de borda a borda.
  const baseClass = folder
    ? 'mpf-side-menu__dropdown__summary-link'
    : submenu
      ? `mpf-side-menu__dropdown__link mpf-side-menu__dropdown__link--${props.review_state}`
      : 'mpf-side-menu__link';
  const activeClass =
    isActive && !folder
      ? submenu
        ? ' mpf-side-menu__dropdown__link--active'
        : ' mpf-side-menu__link--active'
      : '';
  const className = `${baseClass}${activeClass}`;

  // A navegação de contexto expõe `@id` e `href`; usa-se `@id` como fallback
  // para links sem `href` explícito.
  const target = props.href || props['@id'] || '#';

  // Subitens com filhos ganham um chevron decorativo à direita, alinhado
  // pelo `justify-between` do próprio link no SCSS.
  const content = (
    <>
      <span
        className={
          folder
            ? 'mpf-side-menu__dropdown__summary-title'
            : submenu
              ? 'mpf-side-menu__dropdown__link-text'
              : 'mpf-side-menu__link-text'
        }
      >
        {label ?? props.title}
      </span>
      {drill && !folder && (
        <CaretRightIcon
          className="mpf-side-menu__dropdown__link-icon"
          aria-hidden="true"
        />
      )}
    </>
  );

  // Um só componente para os dois tipos de nó: quem decide entre navegação
  // client-side e <a> é o `isInternalURL`, não o `type` do nó — um nó de
  // conteúdo pode apontar para fora, e um nó `link` para dentro.
  return (
    <MPFLink
      href={target}
      title={props.title}
      className={className}
      aria-current={isActive ? 'page' : undefined}
    >
      {content}
    </MPFLink>
  );
};

export default ContextNavigationLink;
